import React, { useState, useContext, useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';
import {
	OfferContext,
	IOfferContext,
	IDkvContext,
	DkvContext,
	ICustomer,
	initialCustomer,
	ButtonState,
	IOffer,
	getOffersByModuleAndMembership,
	getCorrespondingOffer,
	getTotalPrice,
	CustomerService,
	TollService,
	fetchInitialDkvData,
	ISepaInfo,
	PaymentMethods,
	setPaymentMethodsTitle,
	onOrderCompleted,
	IPricingElements,
	IListItemProps,
	IPicture,
	formatPlates,
	getDateString,
	getTimeString,
	IProductSummaryProps,
	getUrlBasePath,
} from '@adac-mp/shared';
import {
	Wrapper,
	Column,
	Row,
	Typography,
	PaymentSelection,
	PriceInformation,
	ProductSummary,
	ProductSummaryElement,
	CustomerData,
	CheckoutButton,
	List,
	BranchOrderFooter,
	Contact,
	TermsAndConditions,
	Info,
	HintMessage,
	Button,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';

import MautboxImage from '../../assets/images/Mautbox-image.jpg';
import { renderTotalPrice } from '../../utils/renderTotalPrice';

export const DkvCheckout = () => {
	const navigate = useNavigate();
	const { viewport } = useViewport();

	const { offer, dkvOffers, updateOffer, updateDkvOffers, updateAllCountries } =
		useContext<IOfferContext>(OfferContext);
	const { inputFields, updateInputFields, updateVehicleRegistration } = useContext<IDkvContext>(DkvContext);

	const [hasMembership, setHasMembership] = useState<boolean>(false);
	const paymentMethod = offer?.paymentMethods[0].id;
	const [payment, setPayment] = useState<PaymentMethods | undefined>(
		paymentMethod ? (String(paymentMethod) as PaymentMethods) : undefined,
	);
	const [customer, setCustomer] = useState<ICustomer | undefined>(undefined);
	const [buttonActive, setButtonActive] = useState<ButtonState>('disabled');
	const [customerFilled, setCustomerFilled] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);

	const correspondingOffer = getCorrespondingOffer(dkvOffers, offer);
	const totalPrice = getTotalPrice(offer, 'Zu zahlen');
	const [sepaInfo, setSepaInfo] = useState<ISepaInfo | undefined>(undefined);
	const [sepaInfoFilled, setSepaInfoFilled] = useState<boolean>(false);
	const [validateFields, setValidateFields] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const errorMsgRef = useRef<HTMLDivElement | null>(null);

	const serviceCatalogName = offer?.serviceCatalogName.trim();
	const duration = `${
		Number(offer?.priceComponents.find((price) => price.type === 'peryear')?.minPeriod) * 12
	} Monate`;
	const licensePlate = inputFields.find((field) => field.documentCode === 'A')?.documentValue;
	const additionalPricingElements: IListItemProps[] = [
		{ label: <>Mindestvertrags­laufzeit</>, value: duration },
		{
			label: 'Folgelaufzeit',
			value: 'unbegrenzt',
		},
	];
	const prependPricingElements: IListItemProps = {
		label: 'Mautgebühren',
		value: 'streckenabhängig je nach Verbrauch',
	};

	const updateMembership = (isMember: boolean) => {
		let dkvMembershipOffers: IOffer[] = [];
		setHasMembership(isMember);

		if (offer) {
			dkvMembershipOffers = getOffersByModuleAndMembership(dkvOffers, isMember, 1);
			const selectedOffer = dkvMembershipOffers.find((dkvOffer) => dkvOffer.fuelTypeId === -1);
			if (selectedOffer) {
				updateOffer(selectedOffer);
			}
		}
	};

	useEffect(() => {
		const offerHash = sessionStorage.getItem('offerHash');
		const customerHash = sessionStorage.getItem('customerHash');
		const customerToken = sessionStorage.getItem('customerToken');
		const memberOfferHash = sessionStorage.getItem('memberOfferHash');

		if (!offerHash) {
			navigate(getUrlBasePath('/camper-mautbox/configure'), { replace: true });
			return;
		}

		if (customerHash && customerToken) {
			CustomerService.get(customerHash, customerToken).then((res) => {
				setCustomer(res.customer);
				updateMembership(Boolean(res.customer.person.memberNo));
			});
		} else {
			setCustomer(initialCustomer);
		}

		if (offerHash && memberOfferHash && !offer) {
			const isMember = sessionStorage.getItem('isMember');
			TollService.getOfferInfo(Boolean(isMember) ? { offerHash: memberOfferHash } : { offerHash }).then((res) => {
				updateOffer(res.offer);
				updateInputFields(res.docItems);
				const resPayment = res.offer.paymentMethods[0].id;
				setPayment(resPayment ? (String(resPayment) as PaymentMethods) : undefined);
				const pictureResponse: IPicture = {
					offerHash,
					archivePictureId: res.archivePictureId,
					pictureIssueId: 1,
				};
				updateVehicleRegistration(pictureResponse);
			});
		}

		if (dkvOffers.length === 0) {
			fetchInitialDkvData(updateDkvOffers, updateAllCountries);
		}
	}, []);

	useEffect(() => {
		setButtonActive(
			customerFilled && isChecked && (!(payment === '3') || (payment === '3' && sepaInfoFilled))
				? 'active'
				: 'disabled',
		);
	}, [customerFilled, isChecked, sepaInfoFilled, payment]);

	useEffect(() => {
		if (error && errorMsgRef.current) {
			const rect = errorMsgRef.current.getBoundingClientRect();
			window.scrollTo({
				top: rect.top + window.scrollY - rect.height,
				behavior: 'smooth',
			});
		}
	}, [error, errorMsgRef.current]);

	const handleClick = () => {
		setValidateFields(true);
		const scrollPos = viewport === 'desktop' ? 355 : 900;
		window.scrollTo({ top: scrollPos, behavior: 'smooth' });
	};

	const correspondingPriceComponents = correspondingOffer?.priceComponents.sort((component1, component2) =>
		component1.priceItemId > component2.priceItemId ? 1 : -1,
	);

	const currentPriceComponents = offer?.priceComponents.sort((component1, component2) =>
		component1.priceItemId > component2.priceItemId ? 1 : -1,
	);

	const getMonthlyAmount = (amount: number) => String((amount / 12).toFixed(2)).replace('.', ',');

	const getYearlyCosts = () => {
		if (!currentPriceComponents) {
			return [];
		}
		const priceComponents = currentPriceComponents.slice(0, 2).map((item) => ({
			label: item.serviceItemName,
			value: item?.amount?.amount ? (
				<>
					{item.amount.amount}€ <br /> ({getMonthlyAmount(item.amount.decimalAmount)}€ / Monat)
				</>
			) : (
				item.serviceItemInfo
			),
			isHighlighted: item.prepaid,
		})) as IListItemProps[];
		priceComponents.splice(1, 0, prependPricingElements);
		return priceComponents.concat(additionalPricingElements);
	};

	const getOneTimeCosts = () => {
		if (!currentPriceComponents) {
			return [];
		}
		return currentPriceComponents.slice(2).map((item, i) => ({
			label: item.serviceItemName,
			value: item?.amount?.amount ? `${item.amount.amount}€` : item.serviceItemInfo,
			previousValue:
				hasMembership &&
				correspondingPriceComponents &&
				item.amount.decimalAmount - correspondingPriceComponents[i + 2]?.amount?.decimalAmount !== 0 &&
				`${correspondingPriceComponents[i + 2]?.amount?.amount}€`,
			discountLabel: '0€ Aktivierungsgebühr für Mitglieder',
			isHighlighted: item.prepaid,
		})) as IListItemProps[];
	};

	const pricingElements: IPricingElements = {
		label: 'Kostenaufteilung',
		totalOfferPrice: totalPrice.amount,
		additionalInfo: [
			{
				title: 'Jährliche Gesamtkosten',
				details: [getYearlyCosts()],
			},
			{
				title: 'Einmalige Gesamtkosten',
				details: [getOneTimeCosts()],
			},
		],
	};

	const productSummaryData: IProductSummaryProps[] = [
		{
			label: 'Ihr Fahrzeug',
			value: licensePlate ? formatPlates(licensePlate) : <Skeleton inline height="100%" width="100%" />,
			additionalInfo: [
				{
					title: 'Fahrzeugscheindaten',
					details: inputFields
						.filter((document) => document.section !== 4 && document.documentCode !== 'A')
						.map((document) => ({
							label: document.documentData,
							fieldName: `Feld ${document.documentCode}`,
							value: String(document.documentValue),
						})),
				},
				{
					title: 'Fahrzeugmasse',
					details: inputFields
						.filter((document) => document.section === 4)
						.map((document) => ({
							label: document.documentData,
							fieldName: `Feld ${document.documentCode}`,
							value: String(document.documentValue),
						})),
				},
			],
		},
		{
			label: 'Kostenaufteilung',
			totalOfferPrice: totalPrice.amount,
			additionalInfo: [
				{
					title: 'Jährliche Gesamtkosten',
					details: getYearlyCosts(),
				},
				{
					title: 'Einmalige Gesamtkosten',
					details: getOneTimeCosts(),
				},
			],
		},
	];

	const getAdditionalData = () => {
		return {
			licenseplate: formatPlates(inputFields.find((field) => field.documentCode === 'A')?.documentValue),
			checkOutDateTime: {
				date: getDateString(new Date()),
				time: getTimeString(new Date()),
			},
		};
	};

	const renderErrorMessage = () =>
		error && <HintMessage ref={errorMsgRef} className="mb-5" variant="error" hints={[{ message: error }]} />;

	const renderCheckoutButton = () => (
		<>
			<TermsAndConditions
				updateButtonState={setIsChecked}
				validateFields={validateFields}
				additionalText={
					<>
						Zur weiteren Bearbeitung Ihrer Buchung übermitteln wir Ihre personenbezogenen Daten an die DKV
						EURO SERVICE GmbH. Diese verarbeitet Ihre Daten in eigener Verantwortung. Rechtsgrundlage
						hierfür ist Art. 6 Abs. 1 b DSGVO. Weitere Informationen zur Verarbeitung Ihrer
						personenbezogenen Daten finden Sie hier:{' '}
						<Button
							underline
							href="https://www.adac.de/fahrzeugwelt/maut-vignette/mautbox/agb-dkv "
							target="_blank"
							variant="tertiary"
							size="sm"
							style={{ padding: 0, display: 'inline' }}>
							Datenschutzregelung der DKV
						</Button>{' '}
						und{' '}
						<Button
							underline
							href="https://www.adac.de/datenschutz-dsgvo/online/"
							target="_blank"
							variant="tertiary"
							size="sm"
							style={{ padding: 0, display: 'inline' }}>
							Datenschutzregelung des ADAC
						</Button>
						.
					</>
				}
				agbs={[
					{
						id: 'agb',
						text: (
							<>
								Es gelten jeweils die{' '}
								<Button
									underline
									href="https://www.adac.de/fahrzeugwelt/maut-vignette/mautbox/agb-dkv/"
									target="_blank"
									variant="tertiary"
									size="sm"
									style={{ padding: 0, display: 'inline' }}>
									AGB (inkl. des Widerrufsrechts) der DKV EURO SERVICE GmbH + Co. KG
								</Button>{' '}
								, der{' '}
								<Button
									underline
									href="https://assets.adac.de/adacfahrzeugplattform/image/upload/v1686748717/Mautportal/AGB_Toll4Europe_zwzkjv.pdf"
									target="_blank"
									variant="tertiary"
									size="sm"
									style={{ padding: 0, display: 'inline' }}>
									Toll4Europe GmbH
								</Button>{' '}
								sowie der{' '}
								<Button
									underline
									href="https://assets.adac.de/adacfahrzeugplattform/image/upload/v1741867881/Mautportal/AGBs/AMR/AMR_AGB_2025_V03_vtsfam.pdf"
									target="_blank"
									variant="tertiary"
									size="sm"
									style={{ padding: 0, display: 'inline' }}>
									ADAC Medien und Reise GmbH
								</Button>
								.
							</>
						),
					},
					{
						id: 'withdrawal',
						text: 'Ich bin damit einverstanden und verlange ausdrücklich, dass vor Ablauf der Widerrufsfrist mit der Ausführung der beauftragten Dienstleistungen begonnen wird. Ich nehme zur Kenntnis, dass ich mein Recht auf Widerruf mit der vollständigen Erbringung der Dienstleistungen verliere.',
					},
				]}
			/>
			{payment && (
				<CheckoutButton
					paymentMethod={payment}
					paymentLabel={
						offer?.paymentMethods.find(({ id }) => String(id) === payment)?.name ?? String(payment)
					}
					variant="dkv"
					buttonState={buttonActive}
					customer={customer}
					sepaData={sepaInfo}
					updateButtonState={setButtonActive}
					additionalData={getAdditionalData()}
					setError={setError}
					onClick={handleClick}
					onOrderCompleted={onOrderCompleted(navigate)}
				/>
			)}
		</>
	);

	return (
		<>
			<Wrapper minSpace>
				{viewport === 'mobile' && (
					<Typography size="h2" component="h1" variant="header" color="title" className="pl-4 mb-4">
						Jetzt bestellen
					</Typography>
				)}
				<Row className="md:flex-row-reverse">
					<Column className="order-2 md:basis-7/12">
						{viewport === 'desktop' && (
							<Typography size="h2" component="h1" variant="header" color="title" className="pl-4 mb-5.5">
								Jetzt bestellen
							</Typography>
						)}
						{viewport === 'desktop' && renderErrorMessage()}
						{offer && (
							<Typography color="title" variant="header" size="xxl" component="h4" className="px-4 mb-4">
								{setPaymentMethodsTitle(offer.paymentMethods)}
							</Typography>
						)}
						{offer ? (
							<PaymentSelection
								paymentMethods={offer?.paymentMethods}
								value={payment}
								variant="dkv"
								validateFields={validateFields}
								updateSepaInfo={setSepaInfo}
								setSepaInfoFilled={setSepaInfoFilled}
								setValue={setPayment}
							/>
						) : (
							<Skeleton width="100%" height={60} className="rounded" />
						)}
						<CustomerData
							hasMembershipBenefit={false}
							variant="dkv"
							customer={customer}
							validateFields={validateFields}
							setCustomerFilled={setCustomerFilled}
							updateCustomer={setCustomer}
						/>
						{viewport === 'mobile' && renderCheckoutButton()}
					</Column>
					<Column className="order-1 mb-5 md:basis-5/12 md:sticky md:self-start md:top-7">
						{viewport === 'mobile' && renderErrorMessage()}
						<ProductSummary
							className="mb-6"
							stacked={false}
							title={serviceCatalogName}
							icon={<img src={MautboxImage} alt="DKV Camper Mautbox" width={86} height={86} />}
							deliveryEstimation="Auslieferung in 7-10 Werktagen">
							{productSummaryData.map((item, index) => (
								<ProductSummaryElement
									key={index}
									type="info"
									role="button"
									label={item.label}
									value={item.value}
									icon={offer ? <Info /> : undefined}
									id={'details'}>
									{offer ? (
										<>
											{item.additionalInfo?.map((element, i: number) => (
												<div key={i}>
													<Typography
														className="pl-4 mt-4 mb-3"
														component="h3"
														size="base"
														color="title">
														{element.title}
													</Typography>
													<List wrapperClassName="mb-4" items={element.details} />
												</div>
											))}
											{item.totalOfferPrice && (
												<PriceInformation
													className="px-4"
													hasMembership={hasMembership}
													totalPrice={totalPrice}
												/>
											)}
										</>
									) : undefined}
								</ProductSummaryElement>
							))}
						</ProductSummary>
						<PriceInformation
							className="bg-gray-600 rounded py-3.5 px-4"
							totalPrice={renderTotalPrice({
								price: totalPrice,
								pricingElements,
								hasMembership,
								showSubHeadline: true,
								paymentHint: true,
							})}
							hasMembership={hasMembership}
						/>
						<HintMessage
							className="mt-4"
							hints={[
								{
									tip: 'Hinweis:',
									message:
										'Der Zahlbetrag wird in Kürze im Rechnungslauf eingezogen. Jahresgebühr sowie Aktivierungsgebühr und Versandkosten werden in der Regel separat abgebucht. Sie werden vor dem Einzug nochmals informiert.',
								},
							]}
						/>
						{payment && viewport === 'desktop' && customer && renderCheckoutButton()}
					</Column>
				</Row>
			</Wrapper>
			<Contact />
			<BranchOrderFooter />
		</>
	);
};
